import { Quote } from 'lucide-react';
import Loop from './Loop';
import testimonialsData from '../data/testimonials.json';

/**
 * Client testimonials as same-size portrait cards: a photo behind each, the
 * client at the top and a short line in their own words at the bottom.
 * Reads from src/data/testimonials.json.
 *
 *  - `short` must be a verbatim excerpt of `quote`. Never paraphrase a client.
 *  - `image` is optional. Cards without one use the charcoal surface so the
 *    row stays uniform until a photo is added under public/testimonials/.
 *  - `imageFit: "contain"` is for logos: the whole logo sits across the middle
 *    of the card and its black background blends into the card surface.
 *    Give logos a pure black background so no box edge shows.
 */
type Testimonial = {
  name: string;
  title?: string;
  short: string;
  quote: string;
  image?: string;
  imageFit?: 'cover' | 'contain';
};

const items = (testimonialsData as { items: Testimonial[] }).items ?? [];

/** Repeat the list so one half of the loop is wider than a large screen. */
const loopItems: Testimonial[] = [];
while (items.length > 0 && loopItems.length < 8) loopItems.push(...items);

function Card({ t, testId }: { t: Testimonial; testId?: string }) {
  return (
    <article
      data-testid={testId}
      title={t.quote}
      className="relative mr-5 aspect-[3/4] w-72 flex-none overflow-hidden rounded-4xl border border-white/10 bg-charcoal sm:w-80"
    >
      {t.image && t.imageFit !== 'contain' ? (
        <img
          src={t.image}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="grain absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(201,168,106,0.18),transparent_60%)]" />
      )}
      {t.image && t.imageFit === 'contain' && (
        <img
          src={t.image}
          alt={t.title ? `${t.title} logo` : ''}
          loading="lazy"
          className="absolute left-1/2 top-1/2 w-[86%] -translate-x-1/2 -translate-y-1/2 mix-blend-lighten"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/15 to-black/85" />

      <div className="relative flex h-full flex-col justify-between p-7">
        <div className="text-center">
          <p className="font-display text-2xl font-light text-white">{t.name}</p>
          {t.title && (
            <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-label text-gold">
              {t.title}
            </p>
          )}
        </div>

        <div className="text-center">
          <Quote
            aria-hidden
            className="mx-auto mb-3 h-5 w-5 text-gold/80"
            strokeWidth={1.5}
          />
          <p className="font-sans text-base leading-relaxed text-white">
            &ldquo;{t.short}&rdquo;
          </p>
        </div>
      </div>
    </article>
  );
}

export default function Testimonials() {
  if (items.length === 0) return null;

  return (
    <section className="bg-black py-32">
      <div className="container-x mb-14 text-center">
        <p className="label mb-6 inline-flex items-center gap-3">
          <span className="h-px w-10 bg-gold" />
          Testimonials
          <span className="h-px w-10 bg-gold" />
        </p>
        <h2 className="font-display text-section font-light text-white text-balance">
          The work, in their words.
        </h2>
      </div>

      <Loop seconds={loopItems.length * 7} label="Testimonials" className="">
        {(copy) =>
          loopItems.map((t, index) => (
            <Card
              key={`${copy}-${t.name}-${index}`}
              t={t}
              testId={copy === 0 && index < items.length ? 'testimonial' : undefined}
            />
          ))
        }
      </Loop>
    </section>
  );
}
